import React, { useState } from "react";
import { X } from "lucide-react";
import { useAppStore, useSettings } from "@/store";
import { toast } from "sonner";

interface SettingsModalProps {
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const settings = useSettings();
  const { updateSettings, resetSettings } = useAppStore();

  const [tempSettings, setTempSettings] = useState(settings);

  const handleSave = (): void => {
    try {
      updateSettings(tempSettings);
     toast("Settings saved successfully");
      onClose();
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Timer Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Timer (minutes)
            </h3>
            <div className="space-y-4">
              {/* Pomodoro */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-gray-700 font-medium">Pomodoro</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={tempSettings.pomodoro.time}
                    onChange={(e) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        pomodoro: {
                          time: parseInt(e.target.value),
                          color: prev.pomodoro.color,
                        },
                      }))
                    }
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-gray-600 text-sm">Color:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={tempSettings.pomodoro.color}
                      onChange={(e) =>
                        setTempSettings((prev) => ({
                          ...prev,
                          pomodoro: {
                            time: prev.pomodoro.time,
                            color: e.target.value,
                          },
                        }))
                      }
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <span
                      className="px-3 py-1 rounded text-sm font-mono text-gray-700 border border-gray-300"
                    >
                      {tempSettings.pomodoro.color}
                    </span>
                  </div>
                </div>
              </div>

              {/* Short Break */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-gray-700 font-medium">Short Break</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={tempSettings.shortBreak.time}
                    onChange={(e) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        shortBreak: {
                          time: parseInt(e.target.value),
                          color: prev.shortBreak.color,
                        },
                      }))
                    }
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-gray-600 text-sm">Color:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={tempSettings.shortBreak.color}
                      onChange={(e) =>
                        setTempSettings((prev) => ({
                          ...prev,
                          shortBreak: {
                            time: prev.shortBreak.time,
                            color: e.target.value,
                          },
                        }))
                      }
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <span
                      className="px-3 py-1 rounded text-sm font-mono text-gray-700 border border-gray-300"
                    >
                      {tempSettings.shortBreak.color}
                    </span>
                  </div>
                </div>
              </div>

              {/* Long Break */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-gray-700 font-medium">Long Break</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={tempSettings.longBreak.time}
                    onChange={(e) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        longBreak: {
                          time: parseInt(e.target.value),
                          color: prev.longBreak.color,
                        },
                      }))
                    }
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-gray-600 text-sm">Color:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={tempSettings.longBreak.color}
                      onChange={(e) =>
                        setTempSettings((prev) => ({
                          ...prev,
                          longBreak: {
                            time: prev.longBreak.time,
                            color: e.target.value,
                          },
                        }))
                      }
                      className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <span
                      className="px-3 py-1 rounded text-sm font-mono text-gray-700 border border-gray-300"
                    >
                      {tempSettings.longBreak.color}
                    </span>
                  </div>
                </div>
              </div>

              {/* Long Break Interval */}
              <div className="flex justify-between items-center pt-2">
                <label className="text-gray-700 font-medium">Long Break Interval</label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={tempSettings.longBreakInterval}
                  onChange={(e) =>
                    setTempSettings((prev) => ({
                      ...prev,
                      longBreakInterval: parseInt(e.target.value),
                    }))
                  }
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Auto-start Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Auto Start
            </h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={tempSettings.autoStartBreaks}
                  onChange={(e) =>
                    setTempSettings((prev) => ({
                      ...prev,
                      autoStartBreaks: e.target.checked,
                    }))
                  }
                  className="mr-3 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-gray-700">Auto start breaks</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={tempSettings.autoStartPomodoros}
                  onChange={(e) =>
                    setTempSettings((prev) => ({
                      ...prev,
                      autoStartPomodoros: e.target.checked,
                    }))
                  }
                  className="mr-3 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-gray-700">Auto start pomodoros</span>
              </label>
            </div>
          </div>

          {/* Notification Settings */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Notifications
            </h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={tempSettings.notifications}
                onChange={(e) =>
                  setTempSettings((prev) => ({
                    ...prev,
                    notifications: e.target.checked,
                  }))
                }
                className="mr-3 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-gray-700">Enable notifications</span>
            </label>
          </div>

          {/* Reset Button */}
          <div className="flex mb-4 flex-col items-start">
            <button
              className="text-lg font-semibold text-red-800 underline"
              onClick={() => {
                resetSettings();
                onClose();
              }}
            >
              Reset Default
            </button>
            <span className="text-gray-700 text-sm">
              (this will clear current settings)
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-8">
          <button
            onClick={handleSave}
            className="flex-1 bg-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-600 transition-colors"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
export type { SettingsModalProps };